import React, { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Card } from '../components/ui/card';
import { AspectRatio } from '../components/ui/aspect-ratio';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import { motion } from 'framer-motion';

export default function Gallery() {
  const [activeTab, setActiveTab] = useState('All');

  const galleryImages = {
    All: [
      { src: 'https://images.unsplash.com/photo-1704428381439-5ed443412617', alt: 'Modern living room' },
      { src: 'https://images.unsplash.com/photo-1682888813795-192fca4a10d9', alt: 'Contemporary kitchen' },
      { src: 'https://images.unsplash.com/photo-1617325247661-675ab4b64ae2', alt: 'Minimalist bedroom' },
      { src: 'https://images.unsplash.com/photo-1519086588705-c935fdedcc14', alt: 'Modern office space' },
      { src: 'https://images.unsplash.com/photo-1704428381312-0579346a779c', alt: 'Elegant living area' },
      { src: 'https://images.unsplash.com/photo-1714860534425-7ce04e013dec', alt: 'Stylish kitchen' },
      { src: 'https://images.unsplash.com/photo-1718717722247-26f4c6c09192', alt: 'Cozy bedroom' },
      { src: 'https://images.unsplash.com/photo-1622128082634-1f9742839291', alt: 'Professional workspace' },
      { src: 'https://images.unsplash.com/photo-1720247520862-7e4b14176fa8', alt: 'Contemporary living' },
      { src: 'https://images.unsplash.com/photo-1665507279644-67d8ed143a84', alt: 'Modern kitchen design' },
      { src: 'https://images.unsplash.com/photo-1699942681763-d1da9f692489', alt: 'Serene bedroom' },
      { src: 'https://images.unsplash.com/photo-1720247520881-672bc136da8a', alt: 'Spacious living room' }
    ],
    Living: [
      { src: 'https://images.unsplash.com/photo-1704428381439-5ed443412617', alt: 'Modern living room' },
      { src: 'https://images.unsplash.com/photo-1704428381312-0579346a779c', alt: 'Elegant living area' },
      { src: 'https://images.unsplash.com/photo-1720247520862-7e4b14176fa8', alt: 'Contemporary living' },
      { src: 'https://images.unsplash.com/photo-1720247520881-672bc136da8a', alt: 'Spacious living room' }
    ],
    Kitchen: [
      { src: 'https://images.unsplash.com/photo-1682888813795-192fca4a10d9', alt: 'Contemporary kitchen' },
      { src: 'https://images.unsplash.com/photo-1714860534425-7ce04e013dec', alt: 'Stylish kitchen' },
      { src: 'https://images.unsplash.com/photo-1665507279644-67d8ed143a84', alt: 'Modern kitchen design' }
    ],
    Bedroom: [
      { src: 'https://images.unsplash.com/photo-1617325247661-675ab4b64ae2', alt: 'Minimalist bedroom' },
      { src: 'https://images.unsplash.com/photo-1718717722247-26f4c6c09192', alt: 'Cozy bedroom' },
      { src: 'https://images.unsplash.com/photo-1699942681763-d1da9f692489', alt: 'Serene bedroom' }
    ],
    Office: [
      { src: 'https://images.unsplash.com/photo-1519086588705-c935fdedcc14', alt: 'Modern office space' },
      { src: 'https://images.unsplash.com/photo-1622128082634-1f9742839291', alt: 'Professional workspace' }
    ]
  };

  return (
    <div className="py-16 md:py-24" data-testid="gallery-page">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-screen-xl">
        <div className="text-center mb-12">
          <h2 className="text-base md:text-lg font-semibold tracking-wide uppercase text-muted-foreground mb-4">
            Portfolio
          </h2>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mb-6">
            Our Work
          </h1>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            Browse through our collection of residential and commercial interior design projects. Each space tells a unique story of transformation and elegance.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="gallery-tabs">
          <TabsList className="mb-8 flex justify-center flex-wrap">
            {Object.keys(galleryImages).map((category) => (
              <TabsTrigger
                key={category}
                value={category}
                data-testid={`gallery-tab-${category.toLowerCase()}`}
              >
                {category}
              </TabsTrigger>
            ))}
          </TabsList>

          {Object.entries(galleryImages).map(([category, images]) => (
            <TabsContent key={category} value={category}>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                {images.map((image, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <Card className="overflow-hidden group card-hover" data-testid="gallery-item-card">
                      <AspectRatio ratio={4/3}>
                        <Zoom>
                          <img
                            src={image.src}
                            alt={image.alt}
                            className="w-full h-full object-cover"
                          />
                        </Zoom>
                      </AspectRatio>
                    </Card>
                  </motion.div>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}